import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import ChatFeedbackService from './ChatFeedbackService';
import {
  parseAuditReport, findingToDraft, applySendMode, draftWarnings, draftToPayload, normalizeChatconeName, buildExcerpt
} from './parseAuditReport';
import { PageChip, SevChip, SEND_MODE_LABEL, BulletText, ChatWithPins, ImageLightbox, CaseReader, modalStyles, pinNumbers } from './chatFeedbackShared';

/**
 * ส่ง Feedback แชท (supervisor only)
 *
 * Every decision is made here. The report HTML is only a source of cases:
 * nothing has to be ticked inside the file first.
 *
 * 1. Upload the daily audit report HTML. Praise cases (sev 'good') and
 *    anything already marked ไม่ใช่ปัญหา in the file are dropped; the rest
 *    arrive UNTICKED, so a case is only ever sent on purpose.
 * 2. Tick the cases to send (ติ๊กทั้งหมด for all of them), then per case
 *    choose the level, who receives it, their role (ต้องปรับ / เพื่อเรียนรู้),
 *    their personal text and the case text.
 * 3. Names in the report are matched to admins by the Chatcone name on the
 *    admin record. Picking an admin for an unmatched name stores it there,
 *    so it is asked once and never again.
 * 4. Send. Already-imported cases are skipped by the backend, so uploading
 *    the same file twice is safe. Drafts that failed stay on screen.
 */

const MODE_HINT = {
  person: 'เฉพาะคนที่พลาด',
  involved: 'ทุกคนที่อยู่ในแชทนี้',
  all: 'แอดมินทุกคน',
  none: 'เก็บไว้ในรายการปัญหาหลังบ้าน'
};
const VERDICT_LABEL = { ok: 'ใช่ ปัญหาจริง', worse: 'หนักกว่าที่รายงาน' };
const SKIP_REASON = { duplicate: 'เคยส่งแล้ว', 'no-recipients': 'ไม่มีคนรับ' };
/** The report's two halves (schema v3 finding.section) */
const SECTION_TABS = [['complaint', 'A · ลูกค้าไม่พอใจ'], ['adlead', 'B · ลูกค้าใหม่จากโฆษณา']];

/** The chat with the report's embedded screenshots swapped in for the Chatcone URLs. */
const withEmbeddedImages = (draft) => {
  const urlMap = {};
  draft.images.forEach((img) => { urlMap[img.origUrl] = img.dataUrl; });
  return draft.chatLog.map((e) => (Array.isArray(e.i) ? { ...e, i: e.i.map((u) => urlMap[u] || u) } : e));
};

const ChatPreview = ({ draft, highlightNames, onEditPin }) => {
  const [fullDay, setFullDay] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const log = useMemo(() => withEmbeddedImages(draft), [draft]);
  const excerpt = buildExcerpt(log, draft.timeRange);
  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap mb-2 text-xs text-gray-500">
        <span>{fullDay || !excerpt.focused ? `ทั้งวัน ${log.length} ข้อความ` : `ช่วงที่เกิดเรื่อง ${excerpt.indexes.length} จาก ${log.length} ข้อความ`}</span>
        {excerpt.focused && (
          <button type="button" onClick={() => setFullDay(!fullDay)} className="text-indigo-600 hover:text-indigo-800 underline">
            {fullDay ? 'ดูเฉพาะช่วงที่เกิดเรื่อง' : 'ดูแชททั้งวัน'}
          </button>
        )}
      </div>
      <div className="text-xs text-gray-400 mb-1.5">กด “+ คอมเมนต์” ข้างข้อความไหนก็ได้ เพื่อชี้ว่าตรงนั้นพลาดอะไร · ข้อความแอดมินใส่ “ควรตอบแบบนี้” ได้ด้วย</div>
      <ChatWithPins chatLog={log} indexes={fullDay ? null : excerpt.indexes} highlightNames={highlightNames} onImageClick={setLightboxUrl} maxHeight="max-h-[34rem]" onEditPin={onEditPin} />
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
};

const Section = ({ label, hint, children }) => (
  <div className="px-5 py-4 border-t border-gray-100">
    <div className="flex items-baseline gap-2 mb-2.5">
      <div className="text-[13px] font-bold text-gray-800">{label}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
    {children}
  </div>
);

function ChatFeedbackImport() {
  const [isSupervisor, setIsSupervisor] = useState(null); // null = checking
  const [admins, setAdmins] = useState([]);
  const [reportDate, setReportDate] = useState(null);
  const [adminVolume, setAdminVolume] = useState({});
  const [drafts, setDrafts] = useState([]);
  const [counts, setCounts] = useState({ total: 0, good: 0, notProblem: 0 });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null); // { index, personKey }
  const [section, setSection] = useState('complaint'); // which half of the report is on screen

  useEffect(() => {
    ChatFeedbackService.getMe()
      .then((res) => setIsSupervisor(!!res.data?.isSupervisor))
      .catch(() => setIsSupervisor(false));
  }, []);

  useEffect(() => {
    if (!isSupervisor) return;
    ChatFeedbackService.getAdmins()
      .then((res) => setAdmins(res.data))
      .catch((err) => toast.error(err.response?.data?.message || 'โหลดรายชื่อแอดมินไม่สำเร็จ'));
  }, [isSupervisor]);

  /** normalised Chatcone name → admin, for matching names in the report */
  const adminByNormName = useMemo(() => {
    const map = {};
    admins.forEach((a) => { if (a.chatconeName) map[normalizeChatconeName(a.chatconeName)] = a; });
    return map;
  }, [admins]);

  if (isSupervisor === null) return <div className="p-8 text-center text-gray-400">กำลังตรวจสอบสิทธิ์...</div>;
  if (!isSupervisor) return <div className="p-8 text-center text-gray-500">หน้านี้เปิดได้เฉพาะหัวหน้า</div>;

  /* ---------- 1. upload ---------- */

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = parseAuditReport(await file.text());
      const good = parsed.findings.filter((f) => f.sev === 'good').length;
      const notProblem = parsed.findings.filter((f) => f.sev !== 'good' && f.verdict === 'no').length;
      const caseDrafts = parsed.findings
        .filter((f) => f.sev !== 'good' && f.verdict !== 'no')
        .map((f) => findingToDraft(f, parsed.img, adminByNormName, admins, parsed.aliases))
        // undecided cases start unticked: someone has to choose on purpose
        .map((d) => (d.reportVerdict || d.sendMode === 'none' ? d : { ...d, include: false }));
      setReportDate(parsed.reportDate);
      setAdminVolume(parsed.adminVolume || {});
      setDrafts(caseDrafts);
      setSection(caseDrafts.some((d) => d.section === 'complaint') || !caseDrafts.length ? 'complaint' : 'adlead');
      setCounts({ total: parsed.findings.length, good, notProblem });
      setResult(null);
      if (!parsed.savedAt) toast.warn('ไฟล์นี้ยังไม่ใช่เวอร์ชันแก้แล้ว เคสทั้งหมดจะขึ้นเป็น "ยังไม่ได้ตัดสิน"');
      toast.success(`อ่านรายงานวันที่ ${parsed.reportDate} ได้ ${caseDrafts.length} เคส`);
    } catch (err) {
      toast.error(err.message || 'อ่านไฟล์ไม่สำเร็จ');
    }
    e.target.value = '';
  };

  /* ---------- 2. edit drafts ---------- */

  // A/B tabs only when the report has ad-lead cases; otherwise every draft is "in the tab"
  const hasTabs = drafts.some((d) => d.section === 'adlead');
  const inTab = (d) => !hasTabs || d.section === section;

  const updateDraft = (index, changes) => setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...changes } : d)));
  const includeAll = (include) => setDrafts((prev) => prev.map((d) => (inTab(d) ? { ...d, include } : d)));
  const setMode = (index, mode) => setDrafts((prev) => prev.map((d, i) => (i === index ? applySendMode(d, mode, admins) : d)));
  /** Add, edit or remove the comment pinned to one chat bubble (undefined values drop the field) */
  const updatePin = (index, entryIndex, changes) => setDrafts((prev) => prev.map((d, i) => {
    if (i !== index) return d;
    const chatLog = d.chatLog.map((e, j) => {
      if (j !== entryIndex) return e;
      const next = { ...e, ...changes };
      // `pin` stays until ลบ, so clearing the text doesn't drop the card mid-edit
      Object.keys(next).forEach((k) => { if (next[k] === undefined) delete next[k]; });
      return next;
    });
    return { ...d, chatLog, showChat: true };
  }));
  const updatePerson = (index, key, changes) => setDrafts((prev) => prev.map((d, i) => (
    i === index ? { ...d, people: d.people.map((p) => (p.key === key ? { ...p, ...changes } : p)) } : d
  )));

  /** Add an admin who was not in the chat (or re-tick one who was) */
  const addPerson = (index, adminId) => {
    const id = parseInt(adminId, 10);
    if (!id) return;
    const admin = admins.find((a) => a.id === id);
    setDrafts((prev) => prev.map((d, i) => {
      if (i !== index) return d;
      if (d.people.some((p) => p.adminId === id)) {
        return { ...d, people: d.people.map((p) => (p.adminId === id ? { ...p, checked: true } : p)) };
      }
      return { ...d, people: [...d.people, { key: `admin-${id}`, chatconeName: null, adminId: id, nickname: admin?.nickname || `#${id}`, role: 'learn', what: '', next: '', inChat: false, manual: true, checked: true }] };
    }));
  };

  /**
   * A name in the report nobody is mapped to. Picking an admin stores the
   * name on that admin (the same Chatcone Name field as Admin Settings), so
   * every future report matches it without asking again, and patches the
   * drafts already on screen.
   */
  const resolveName = async (chatconeName, adminId) => {
    const id = parseInt(adminId, 10);
    if (!id) return;
    const admin = admins.find((a) => a.id === id);
    // One Chatcone name per admin: replacing one is a real change, so confirm it.
    if (admin?.chatconeName && admin.chatconeName !== chatconeName
      && !window.confirm(`${admin.nickname} มีชื่อ Chatcone เดิมว่า "${admin.chatconeName}"\nจะเปลี่ยนเป็น "${chatconeName}" ใช่ไหม?`)) return;

    try {
      await ChatFeedbackService.setChatconeName(id, chatconeName);
      const res = await ChatFeedbackService.getAdmins();
      setAdmins(res.data);
      const norm = normalizeChatconeName(chatconeName);
      setDrafts((prev) => prev.map((d) => ({
        ...d,
        people: d.people.map((p) => (p.key === norm ? { ...p, adminId: id, nickname: admin?.nickname || p.nickname } : p))
      })));
      toast.success(`บันทึกแล้ว "${chatconeName}" คือ ${admin?.nickname} · ครั้งหน้าระบบจับคู่ให้เอง`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'บันทึกชื่อไม่สำเร็จ');
    }
  };

  /* ---------- 3. send ---------- */

  // With A/B tabs each tab is its own batch: counts, warnings and send cover the open tab only
  const tabDrafts = drafts.filter(inTab);
  const included = tabDrafts.filter((d) => d.include);
  const unresolvedNames = [...new Set(included.flatMap((d) => d.people.filter((p) => p.checked && !p.adminId).map((p) => p.chatconeName)))];
  // Ticked to send but nobody will receive it. Blocks the send rather than
  // being dropped quietly, so a case is never lost between tick and send.
  const noRecipient = included.filter((d) => d.sendMode !== 'none' && !d.people.some((p) => p.checked && p.adminId));
  const sendable = included.filter((d) => !noRecipient.includes(d));
  const backofficeCount = sendable.filter((d) => d.sendMode === 'none').length;
  const recipientCount = new Set(sendable.flatMap((d) => d.people.filter((p) => p.checked && p.adminId).map((p) => p.adminId))).size;
  const needReview = included.filter((d) => draftWarnings(d).length > 0).length;

  const handleSend = async () => {
    if (included.length === 0) return toast.error('ยังไม่ได้ติ๊กเคสที่จะส่ง');
    if (noRecipient.length) return toast.error(`มี ${noRecipient.length} เคสที่ติ๊กไว้แต่ยังไม่ได้เลือกคนรับ · เลือกคนรับ หรือติ๊กเคสออก`);
    if (unresolvedNames.length) return toast.error('ยังมีชื่อที่ไม่รู้ว่าเป็นใครในระบบ เลือกคนก่อนถึงจะส่งได้');
    setSending(true);
    try {
      const res = await ChatFeedbackService.importCases({ reportDate, adminVolume, cases: sendable.map(draftToPayload) });
      const { created, skipped, errors } = res.data;
      setResult(res.data);
      toast.success(`ส่งแล้ว ${created.length} เคส${skipped.length ? ` · ข้าม ${skipped.length}` : ''}${errors.length ? ` · ผิดพลาด ${errors.length}` : ''}`);
      // Keep only the drafts that failed, so the supervisor can fix and resend them.
      // The other tab's drafts weren't sent, so they stay.
      const failed = new Set(errors.map((x) => x.sourceId));
      setDrafts((prev) => prev.filter((d) => !inTab(d) || failed.has(d.sourceId)));
    } catch (err) {
      toast.error(err.response?.data?.message || 'ส่งไม่สำเร็จ');
    } finally {
      setSending(false);
    }
  };

  /* ---------- render ---------- */

  const renderDraft = (d, index) => {
    const warnings = d.include ? draftWarnings(d) : [];
    const checked = d.people.filter((p) => p.checked);
    const fixNames = checked.filter((p) => p.role === 'fix' && p.chatconeName).map((p) => p.chatconeName);
    const pinCount = Object.keys(pinNumbers(d.chatLog)).length;

    if (!d.expanded) {
      const summary = d.sendMode === 'none' ? SEND_MODE_LABEL.none : `${SEND_MODE_LABEL[d.sendMode]}: ${checked.map((p) => p.nickname).join(', ') || 'ยังไม่เลือก'}`;
      return (
        <div key={d.sourceId} onClick={() => updateDraft(index, { expanded: true })}
          className={`bg-white rounded-xl border px-4 py-3 cursor-pointer hover:shadow-sm ${warnings.length ? 'border-amber-400' : d.include ? 'border-gray-300' : 'border-gray-200 opacity-60'}`}>
          <div className="flex items-start gap-3">
            <input type="checkbox" checked={d.include} onClick={(e) => e.stopPropagation()} onChange={(e) => updateDraft(index, { include: e.target.checked })} className="mt-1 h-5 w-5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-gray-800">{d.title || d.category}</div>
              <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500">
                <PageChip page={d.page} /><SevChip sev={d.severity} /><span>{d.category}</span>
                {d.timeRange && <span>· {d.timeRange}</span>}
                <span className="text-gray-700">· {summary}</span>
              </div>
              {warnings.length > 0 && <div className="mt-1.5 text-xs text-amber-800">⚠ {warnings.join(' · ')}</div>}
            </div>
            <span className="text-xs text-gray-400 mt-1">แก้ ▾</span>
          </div>
        </div>
      );
    }

    return (
      <div key={d.sourceId} className={`bg-white rounded-xl border-2 shadow-sm ${d.include ? 'border-indigo-300' : 'border-gray-200'}`}>
        <div className="px-5 pt-4 pb-3 flex items-start gap-3">
          <input type="checkbox" checked={d.include} onChange={(e) => updateDraft(index, { include: e.target.checked })} className="mt-1.5 h-5 w-5 flex-shrink-0" title="ติ๊กออก = ไม่ส่งเคสนี้" />
          <div className="flex-1 min-w-0">
            <input value={d.title} onChange={(e) => updateDraft(index, { title: e.target.value })} placeholder="หัวข้อเคส" className="w-full text-[15px] font-semibold text-gray-800 border-b border-transparent focus:border-indigo-300 focus:outline-none" />
            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500">
              <PageChip page={d.page} />
              <select value={d.severity} onChange={(e) => updateDraft(index, { severity: e.target.value })} className="border rounded px-1 py-0.5 text-xs">
                {['critical', 'high', 'med', 'low'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <span>{d.category}</span>{d.timeRange && <span>· {d.timeRange}</span>}
              {d.reportVerdict && <span className="text-gray-700">· ผลตรวจ: {VERDICT_LABEL[d.reportVerdict]}</span>}
            </div>
            {warnings.length > 0 && <div className="mt-1.5 text-xs text-amber-800">⚠ {warnings.join(' · ')}</div>}
          </div>
          {checked.length > 0 && (
            <button type="button" onClick={() => setPreview({ index, personKey: (checked.find((p) => p.role === 'fix') || checked[0]).key })} className="text-xs text-indigo-600 hover:text-indigo-800 whitespace-nowrap">ดูแบบที่แอดมินจะเห็น</button>
          )}
          <button type="button" onClick={() => updateDraft(index, { expanded: false })} className="text-xs text-gray-500 hover:text-gray-800">ย่อ ▴</button>
        </div>

        <Section label="ส่งถึงใคร">
          <div className="flex gap-1.5 flex-wrap mb-3">
            {Object.keys(MODE_HINT).map((m) => (
              <button key={m} type="button" onClick={() => setMode(index, m)} title={MODE_HINT[m]}
                className={`px-3 py-1 rounded-full text-xs border ${d.sendMode === m ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}>
                {SEND_MODE_LABEL[m]}
              </button>
            ))}
          </div>
          {d.sendMode !== 'none' && (
            <div className="space-y-2">
              {d.people.map((p) => (
                <div key={p.key} className={`flex items-center gap-2 flex-wrap text-sm ${p.checked ? '' : 'opacity-50'}`}>
                  <input type="checkbox" checked={p.checked} onChange={(e) => updatePerson(index, p.key, { checked: e.target.checked })} className="h-4 w-4" />
                  <span className="font-medium">{p.nickname}</span>
                  {p.chatconeName && p.chatconeName !== p.nickname && <span className="text-xs text-gray-400">({p.chatconeName})</span>}
                  {!p.adminId && (
                    <select value="" onChange={(e) => resolveName(p.chatconeName, e.target.value)} title="เลือกแล้วระบบจะจำชื่อนี้ไว้กับแอดมินคนนั้นถาวร" className="border border-amber-400 rounded px-1 py-0.5 text-xs bg-amber-50">
                      <option value="">ชื่อนี้คือใคร? (จำไว้ถาวร)</option>
                      {admins.map((a) => <option key={a.id} value={a.id}>{a.nickname}{a.chatconeName ? ` (${a.chatconeName})` : ''}</option>)}
                    </select>
                  )}
                  <span className="inline-flex rounded overflow-hidden border border-gray-300 text-xs">
                    <button type="button" onClick={() => updatePerson(index, p.key, { role: 'fix' })} className={`px-2 py-0.5 ${p.role === 'fix' ? 'bg-red-600 text-white' : 'bg-white text-gray-600'}`}>ต้องปรับ</button>
                    <button type="button" onClick={() => updatePerson(index, p.key, { role: 'learn' })} className={`px-2 py-0.5 border-l border-gray-300 ${p.role === 'learn' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}>เพื่อเรียนรู้</button>
                  </span>
                </div>
              ))}
              <select value="" onChange={(e) => addPerson(index, e.target.value)} className="border rounded px-2 py-1 text-xs text-gray-600">
                <option value="">+ เพิ่มคน</option>
                {admins.filter((a) => !d.people.some((p) => p.adminId === a.id && p.checked)).map((a) => <option key={a.id} value={a.id}>{a.nickname}</option>)}
              </select>
            </div>
          )}
        </Section>

        {d.sendMode !== 'none' && checked.some((p) => p.role === 'fix') && (
          <Section label="ข้อความรายคน (คนที่ต้องปรับ)" hint="แอดมินเห็นส่วนนี้เป็นอันดับแรก แก้ได้ก่อนส่ง">
            <div className="space-y-3">
              {checked.filter((p) => p.role === 'fix').map((p) => (
                <div key={p.key} className="rounded-lg border border-purple-200 bg-purple-50 p-3">
                  <div className="text-xs font-bold text-purple-800 mb-1">{p.nickname}</div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">พลาดตรงไหน</label>
                  <textarea value={p.what} onChange={(e) => updatePerson(index, p.key, { what: e.target.value })} rows={2} className="w-full border rounded px-2 py-1 text-sm" placeholder="ถ้าเว้นว่าง จะเห็นแค่ข้อความรวมของเคส" />
                  <label className="block text-xs font-semibold text-gray-600 mb-1 mt-2">ครั้งหน้าให้ทำแบบนี้</label>
                  <textarea value={p.next} onChange={(e) => updatePerson(index, p.key, { next: e.target.value })} rows={2} className="w-full border rounded px-2 py-1 text-sm" />
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section label="ข้อความรวมของเคส" hint="ทุกคนที่ได้รับจะเห็นเหมือนกัน">
          <label className="block text-xs font-semibold text-red-700 mb-1">เกิดอะไรขึ้น</label>
          <textarea value={d.issue} onChange={(e) => updateDraft(index, { issue: e.target.value })} rows={4} className="w-full border rounded px-2 py-1 text-sm" />
          <label className="block text-xs font-semibold text-green-700 mb-1 mt-2">มาตรฐานที่ต้องใช้ต่อไป</label>
          <textarea value={d.fix} onChange={(e) => updateDraft(index, { fix: e.target.value })} rows={3} className="w-full border rounded px-2 py-1 text-sm" />
          {d.reportVerdictNote && <div className="mt-2 text-xs text-gray-500">หมายเหตุจากผลตรวจ: <BulletText text={d.reportVerdictNote} /></div>}
        </Section>

        <Section label="แชทจริงกับลูกค้า" hint={`${d.chatLog.length} ข้อความ · รูป ${d.images.length}${pinCount ? ` · คอมเมนต์ ${pinCount} จุด` : ''}`}>
          {d.showChat || pinCount > 0
            ? <ChatPreview draft={d} highlightNames={fixNames} onEditPin={(entryIndex, changes) => updatePin(index, entryIndex, changes)} />
            : <button type="button" onClick={() => updateDraft(index, { showChat: true })} className="text-xs text-indigo-600 hover:text-indigo-800">ดูแชทและรูป · ใส่คอมเมนต์</button>}
        </Section>
      </div>
    );
  };

  const previewDraft = preview ? drafts[preview.index] : null;
  const previewPerson = previewDraft ? previewDraft.people.find((p) => p.key === preview.personKey) : null;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">ส่ง Feedback แชท</h1>
        <p className="text-sm text-gray-500 mt-1">อัปโหลดไฟล์รายงาน ติ๊กเคสที่จะส่ง เลือกคนรับ แล้วกดส่ง · ตัดสินทุกอย่างที่หน้านี้ ไม่ต้องติ๊กในไฟล์ก่อน · ชื่อใน Chatcone ตั้งที่ <Link to="/setting/admin" className="text-indigo-600 underline">ตั้งค่าแอดมิน</Link></p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="bg-indigo-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-indigo-700 cursor-pointer">
          เลือกไฟล์รายงาน (.html)
          <input type="file" accept=".html,.htm" onChange={handleFile} className="hidden" />
        </label>
        {reportDate && <span className="text-sm text-gray-600">รายงานวันที่ <b>{reportDate}</b> · ทั้งหมด {counts.total} · ทำดี {counts.good} · ไม่ใช่ปัญหา {counts.notProblem}</span>}
      </div>

      {unresolvedNames.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          ชื่อในรายงานที่ยังไม่รู้ว่าเป็นใคร: <b>{unresolvedNames.join(', ')}</b> · เลือกคนในเคสด้านล่าง ระบบจะจำไว้ถาวร ครั้งหน้าไม่ต้องเลือกอีก
        </div>
      )}

      {noRecipient.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          <b>ส่งไม่ได้:</b> ติ๊กไว้ {noRecipient.length} เคส แต่ยังไม่ได้เลือกคนรับ — เลือกคนรับ หรือติ๊กเคสออก
          <ul className="list-disc pl-5 mt-1">
            {noRecipient.map((d) => <li key={d.sourceId}>{d.title || d.category}</li>)}
          </ul>
        </div>
      )}

      {drafts.length > 0 && (
        <>
          <div className="flex items-center gap-3 flex-wrap text-sm text-gray-600">
            <span>
              เคสทั้งหมด <b>{tabDrafts.length}</b> · ติ๊กไว้ <b>{included.length}</b> · พร้อมส่ง <b>{sendable.length}</b> ถึง <b>{recipientCount}</b> คน{backofficeCount ? ` · หลังบ้าน ${backofficeCount}` : ''}{noRecipient.length ? ` · ยังไม่มีคนรับ ${noRecipient.length}` : ''}{needReview ? ` · ต้องดูก่อน ${needReview}` : ''}
            </span>
            <span className="ml-auto flex gap-2">
              <button type="button" onClick={() => includeAll(true)} className="border border-gray-300 rounded px-2 py-1 text-xs hover:bg-gray-50">ติ๊กทั้งหมด</button>
              <button type="button" onClick={() => includeAll(false)} className="border border-gray-300 rounded px-2 py-1 text-xs hover:bg-gray-50">ล้างทั้งหมด</button>
            </span>
          </div>
          {hasTabs && (
            <div className="flex gap-2 border-b border-gray-200" role="tablist">
              {SECTION_TABS.map(([key, label]) => {
                const inTab = drafts.filter((d) => d.section === key);
                return (
                  <button key={key} type="button" role="tab" aria-selected={section === key} onClick={() => setSection(key)}
                    className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${section === key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                    {label} <span className="font-normal text-gray-400">· {inTab.length} เคส · ติ๊ก {inTab.filter((d) => d.include).length}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="space-y-2">{drafts.map((d, i) => (inTab(d) ? renderDraft(d, i) : null))}</div>
          <button type="button" onClick={handleSend} disabled={sending || included.length === 0 || noRecipient.length > 0 || unresolvedNames.length > 0}
            className="bg-indigo-600 text-white rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {sending ? 'กำลังส่ง...' : `ส่ง ${sendable.length} เคส`}
          </button>
        </>
      )}

      {result && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm space-y-1">
          <div className="font-semibold text-gray-800">ผลการส่ง</div>
          <div className="text-green-700">ส่งแล้ว {result.created.length} เคส</div>
          {result.skipped.map((s) => <div key={`s-${s.sourceId}`} className="text-gray-500">ข้าม {s.sourceId}: {SKIP_REASON[s.reason] || s.reason}</div>)}
          {result.errors.map((e) => <div key={`e-${e.sourceId}`} className="text-red-600">ผิดพลาด {e.sourceId}: {e.message}</div>)}
        </div>
      )}

      <Modal isOpen={!!preview} onRequestClose={() => setPreview(null)} style={modalStyles} ariaHideApp={false}>
        {previewDraft && (
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-semibold text-gray-800">แบบที่ {previewPerson?.nickname} จะเห็น</div>
              <button type="button" onClick={() => setPreview(null)} className="ml-auto text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>
            <CaseReader
              caseData={{ ...previewDraft, reportDate, chatLog: withEmbeddedImages(previewDraft) }}
              recipient={previewPerson ? { status: 'pending', role: previewPerson.role, personalWhat: previewPerson.what, personalNext: previewPerson.next } : null}
              highlightNames={previewPerson?.chatconeName ? [previewPerson.chatconeName] : []}
              viewerName={previewPerson?.nickname}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ChatFeedbackImport;
